import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Download, RefreshCw, Users, Trash2 } from "lucide-react";

const BAND_STYLE = {
  green: "bg-green-100 text-green-800 border-green-200",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
  red: "bg-red-100 text-red-800 border-red-200",
  none: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

const STATUS_STYLE = {
  done: "bg-green-100 text-green-800 border-green-200",
  processing: "bg-blue-100 text-blue-800 border-blue-200",
  queued: "bg-amber-100 text-amber-800 border-amber-200",
  failed: "bg-red-100 text-red-800 border-red-200",
};

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] || "bg-zinc-100 text-zinc-600 border-zinc-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${style}`} data-testid="lead-status-badge">
      {status || "—"}
    </span>
  );
}

const bandOf = (s) => (s == null ? "none" : s > 75 ? "green" : s >= 60 ? "amber" : "red");

function ScoreBadge({ score }) {
  const b = bandOf(score);
  return (
    <span className={`inline-flex items-center justify-center min-w-[2.5rem] px-2 py-0.5 rounded-full text-xs font-semibold border ${BAND_STYLE[b]}`} data-testid="lead-score-badge">
      {score == null ? "—" : score}
    </span>
  );
}

const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [band, setBand] = useState("all");
  const [role, setRole] = useState("");
  const [trial, setTrial] = useState("all");
  const [range, setRange] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (band !== "all") params.band = band;
      if (role.trim()) params.target_role = role.trim();
      if (trial !== "all") params.trial_started = trial;
      const [leadsRes, funnelRes] = await Promise.all([
        api.get("/admin/leads", { params }),
        api.get("/admin/funnel", { params: range === "all" ? {} : { days: range } }),
      ]);
      setLeads(leadsRes.data);
      setFunnel(funnelRes.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [band, role, trial, range]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [band, trial, range]);

  const stats = useMemo(() => {
    const total = leads.length;
    const trials = leads.filter((l) => l.trial_started).length;
    const strong = leads.filter((l) => bandOf(l.overall_score) === "green").length;
    return { total, trials, strong, rate: total ? Math.round((trials / total) * 100) : 0 };
  }, [leads]);

  const exportCsv = () => {
    const cols = ["created_at", "name", "email", "target_role", "seniority", "overall_score", "status", "trial_started", "marketing_consent", "utm"];
    const rows = [cols.join(",")];
    leads.forEach((l) => rows.push(cols.map((c) => csvCell(l[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jobsmith-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteLead = async (id) => {
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete lead.");
    }
  };

  const purgeCvs = async () => {
    try {
      const { data } = await api.post("/admin/leads/purge-cvs");
      toast.success(`Purged CV data for ${data.purged} old lead(s).`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to purge.");
    }
  };

  if (error) return (
    <div className="bg-white border border-zinc-200 rounded-xl py-20 text-center" data-testid="leads-error">
      <p className="text-lg font-semibold text-zinc-900">{error}</p>
      <p className="text-sm text-muted-foreground mt-1">You don't have permission to view this page.</p>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="admin-leads-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-7 w-7 text-accent" strokeWidth={1.75} /> Leads
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Free CV analysis submissions and trial conversions.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="leads-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" data-testid="leads-purge-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Purge old CVs</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Purge CV files older than 30 days?</AlertDialogTitle>
                <AlertDialogDescription>This permanently deletes the stored CV file and parsed text for leads older than 30 days (GDPR retention). Lead names, emails and scores are kept. This runs automatically every day.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="purge-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={purgeCvs} data-testid="purge-confirm">Purge now</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={exportCsv} disabled={!leads.length} data-testid="leads-export-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {funnel && (
        <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="funnel-analytics">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Conversion funnel</p>
            <div className="flex gap-1 bg-zinc-100 rounded-lg p-0.5" data-testid="funnel-range">
              {[["all", "All time"], ["30", "30 days"], ["7", "7 days"]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setRange(v)}
                  className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${range === v ? "bg-white shadow-sm text-zinc-900" : "text-muted-foreground hover:text-zinc-700"}`}
                  data-testid={`funnel-range-${v}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-stretch gap-2 sm:gap-4">
            {[
              { label: "Page views", value: funnel.views, testid: "funnel-views", w: "w-full" },
              { label: "CV submitted", value: funnel.submits, testid: "funnel-submits", w: "w-4/5", rate: funnel.view_to_submit, from: "of views" },
              { label: "Started trial", value: funnel.trials, testid: "funnel-trials", w: "w-3/5", rate: funnel.submit_to_trial, from: "of submits" },
            ].map((s, i) => (
              <div key={s.label} className="flex-1">
                {s.rate != null && (
                  <div className="text-center mb-1"><span className="text-xs font-medium text-accent" data-testid={`${s.testid}-rate`}>↳ {s.rate}% {s.from}</span></div>
                )}
                {s.rate == null && <div className="h-5 mb-1" />}
                <div className={`mx-auto ${s.w} bg-gradient-to-b from-accent/15 to-accent/5 border border-accent/20 rounded-lg py-5 text-center`}>
                  <p className="text-3xl font-bold font-heading" data-testid={s.testid}>{s.value}</p>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{s.label}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-muted-foreground mt-4">
            Overall view → trial conversion: <span className="font-semibold text-zinc-900" data-testid="funnel-overall">{funnel.overall}%</span>
          </p>
          {funnel.by_source?.length > 0 && (
            <div className="mt-6 border-t border-zinc-100 pt-4" data-testid="funnel-by-source">
              <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">By source (UTM)</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-zinc-100">
                      <th className="py-2 pr-4 font-semibold">Source</th>
                      <th className="py-2 px-4 font-semibold text-right">Views</th>
                      <th className="py-2 px-4 font-semibold text-right">Submits</th>
                      <th className="py-2 px-4 font-semibold text-right">Trials</th>
                      <th className="py-2 pl-4 font-semibold text-right">Conv.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funnel.by_source.map((s) => (
                      <tr key={s.source} className="border-b border-zinc-50" data-testid={`source-row-${s.source}`}>
                        <td className="py-2 pr-4 font-medium capitalize">{s.source}</td>
                        <td className="py-2 px-4 text-right text-muted-foreground">{s.views}</td>
                        <td className="py-2 px-4 text-right text-muted-foreground">{s.submits}</td>
                        <td className="py-2 px-4 text-right text-muted-foreground">{s.trials}</td>
                        <td className="py-2 pl-4 text-right font-semibold">{s.conversion}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Total leads", value: stats.total, testid: "stat-total" },
          { label: "Started trial", value: stats.trials, testid: "stat-trials" },
          { label: "Conversion", value: `${stats.rate}%`, testid: "stat-rate" },
          { label: "Strong CVs", value: stats.strong, testid: "stat-strong" },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={s.testid}>
            <p className="text-2xl font-bold font-heading">{s.value}</p>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-4 bg-white border border-zinc-200 rounded-xl p-4">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider">Score band</Label>
          <Select value={band} onValueChange={setBand}>
            <SelectTrigger className="w-40" data-testid="filter-band"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All bands</SelectItem>
              <SelectItem value="green">Strong (76+)</SelectItem>
              <SelectItem value="amber">Getting there (60–75)</SelectItem>
              <SelectItem value="red">Needs work (&lt;60)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold uppercase tracking-wider">Trial</Label>
          <Select value={trial} onValueChange={setTrial}>
            <SelectTrigger className="w-40" data-testid="filter-trial"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              <SelectItem value="true">Started trial</SelectItem>
              <SelectItem value="false">Not yet</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 flex-1 min-w-[180px]">
          <Label className="text-xs font-semibold uppercase tracking-wider">Role contains</Label>
          <div className="flex gap-2">
            <Input value={role} onChange={(e) => setRole(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="e.g. Analyst" data-testid="filter-role" />
            <Button variant="secondary" onClick={load} data-testid="filter-apply-btn">Apply</Button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-accent" /></div>
        ) : leads.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="leads-empty">No leads match these filters yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Score</th>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Target role</th>
                  <th className="px-4 py-3 font-semibold">Seniority</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Trial</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3"><ScoreBadge score={l.overall_score} /></td>
                    <td className="px-4 py-3 font-medium">{l.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{l.email}</td>
                    <td className="px-4 py-3">{l.target_role}</td>
                    <td className="px-4 py-3 text-muted-foreground">{l.seniority || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3">{l.trial_started ? <span className="text-green-700 font-medium text-xs">Yes</span> : <span className="text-muted-foreground text-xs">—</span>}</td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
                            <AlertDialogDescription>This permanently removes {l.name} ({l.email}) and their stored CV file. This cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteLead(l.id)} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
